// apps/web/src/features/crm/leads/activity/components/LeadFileDrawer.tsx
//
// Capture drawer for a file attachment (#1809). Reuses the exact 3-step
// upload flow from `LeadFilesSection.UploadControl` (mint presigned URL → PUT
// bytes → bind metadata row), surfaced inside an `EditorDrawer`. A native file
// input has no editable text to lose, so `dirty={false}` is correct; the
// EditorDrawer shell is still used per the issue's capture-drawer convention.
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LEAD_FILE_ALLOWED_MIME } from '@magimp/shared';
import { useLeadFiles } from '@magimp/shared-hooks';
import { Button, EditorDrawer, ErrorState, LoadingState } from '../../../../../ui/index.js';
import { formatSize } from '../../../../../i18n/index.js';
import { errorMessage } from '../../../../../api/errors.js';
import { useLeadFileUpload } from '../useLeadFileUpload.js';

const ACCEPT_HEADER = LEAD_FILE_ALLOWED_MIME.join(',');

export interface LeadFileDrawerProps {
  leadId: string;
  onClose: () => void;
}

export function LeadFileDrawer({ leadId, onClose }: LeadFileDrawerProps) {
  const { t } = useTranslation(['leads', 'common']);
  const filesQ = useLeadFiles(leadId);

  if (filesQ.isPending) {
    return (
      <EditorDrawer dirty={false} onConfirmClose={onClose} width={420}>
        <div style={{ padding: 16 }}>
          <LoadingState />
        </div>
      </EditorDrawer>
    );
  }
  if (filesQ.error) {
    return (
      <EditorDrawer dirty={false} onConfirmClose={onClose} width={420}>
        <div style={{ padding: 16 }}>
          <ErrorState
            title={t('common:states.loadError')}
            text={errorMessage(filesQ.error)}
            onRetry={() => void filesQ.refetch()}
          />
        </div>
      </EditorDrawer>
    );
  }

  const { usage } = filesQ.data;
  return (
    <FileDrawerBody
      leadId={leadId}
      onClose={onClose}
      capReached={usage.count >= usage.maxPerLead}
      maxBytes={usage.maxBytesPerFile}
    />
  );
}

function FileDrawerBody({
  leadId,
  onClose,
  capReached,
  maxBytes,
}: {
  leadId: string;
  onClose: () => void;
  capReached: boolean;
  maxBytes: number;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const { upload, uploading } = useLeadFileUpload(leadId, { maxBytes, onUploaded: onClose });
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <EditorDrawer dirty={false} busy={uploading} onConfirmClose={onClose} width={420}>
      <div
        data-testid="lead-file-drawer"
        style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
          {t('leads:activity.files.upload')}
        </h2>
        <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>
          {t('leads:activity.files.maxSize', { size: formatSize(maxBytes) })}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_HEADER}
          disabled={uploading || capReached}
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            e.currentTarget.value = '';
            if (file) void upload(file);
          }}
          style={{ display: 'none' }}
          data-testid="lead-file-drawer-input"
        />
        {capReached ? (
          <div style={{ fontSize: 12, color: 'var(--fg-2)' }} data-testid="lead-file-drawer-cap">
            {t('leads:activity.files.capReached')}
          </div>
        ) : (
          <Button
            kind="primary"
            icon="upload"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="lead-file-drawer-upload"
          >
            {uploading ? t('leads:activity.files.uploading') : t('leads:activity.files.upload')}
          </Button>
        )}
      </div>
    </EditorDrawer>
  );
}
